import { useState, useEffect, useMemo } from 'react';
import {
  X, Users, Building2, GraduationCap, Layers, Search, Check, AlertCircle,
  History, Trash2, RefreshCw, User, UsersRound, Filter, ChevronDown, ChevronUp, Copy
} from 'lucide-react';
import {
  useFeeAssignments,
  useAssignToStudents,
  useAssignToFiliere,
  useAssignToCampus,
  useRemoveAssignment
} from '../../hooks/useAncillaryFees';
import { getAcademicYears } from '@/services/academicManagementService';
import { formatCurrency, getCampusInfo, fetchStudentsForFeeAssignment, fetchFilieresGroupedForTuition } from '@/services/tuitionService';
import type { AncillaryFee, AncillaryFeeAssignment, GroupedFiliereForTuition } from '@/types/tuition.types';
import { loadFamilyGroups } from './assignGroupFilters';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

interface FilterOption {
  value: string;
  label: string;
  isCurrent?: boolean;
  code?: string;
  nombreAnnees?: number;
}

interface StudentOption {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

interface Props {
  fee: AncillaryFee;
  onClose: () => void;
  onSuccess: () => void;
}

type TabType = 'assign' | 'history';

const AncillaryFeeAssignModal: React.FC<Props> = ({ fee, onClose, onSuccess: _onSuccess }) => {
  const [activeTab, setActiveTab] = useState<TabType>('assign');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  // Data loading states
  const [academicYears, setAcademicYears] = useState<FilterOption[]>([]);
  const [campuses, setCampuses] = useState<FilterOption[]>([]);
  const [filieres, setFilieres] = useState<GroupedFiliereForTuition[]>([]);
  const [groups, setGroups] = useState<FilterOption[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Form states - cascading filters
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedFiliere, setSelectedFiliere] = useState(''); // parent filière id
  const [selectedNiveau, setSelectedNiveau] = useState<number | ''>('');
  const [selectedOption, setSelectedOption] = useState(''); // option filière id (narrows within niveau)
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [allowMultiple, setAllowMultiple] = useState(false);
  // How the fee lands in the student's payment schedule.
  const [paymentMode, setPaymentMode] = useState<'DISTRIBUTE' | 'IMMEDIATE'>('DISTRIBUTE');

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // Hooks
  const { data: assignmentsData, isLoading: isLoadingAssignments, refetch: refetchAssignments } = useFeeAssignments(fee.id, { includeInactive: true });
  const assignToStudentsMutation = useAssignToStudents();
  const assignToFiliereMutation = useAssignToFiliere();
  const assignToCampusMutation = useAssignToCampus();
  const removeAssignmentMutation = useRemoveAssignment(fee.id);

  const isSubmitting = assignToStudentsMutation.isPending || assignToFiliereMutation.isPending || assignToCampusMutation.isPending;

  // Get the selected (parent) filière's data for niveau + options dropdowns
  const selectedFiliereData = useMemo(() => {
    return filieres.find(f => f.id === selectedFiliere);
  }, [filieres, selectedFiliere]);

  const niveauOptions = useMemo(() => {
    if (!selectedFiliereData?.nombreAnnees) return [];
    return Array.from({ length: selectedFiliereData.nombreAnnees }, (_, i) => i + 1);
  }, [selectedFiliereData]);

  // Options that exist at the currently selected niveau. Only shown once a
  // niveau where the specialisation starts is chosen (per the option's
  // startNiveau, falling back to the parent's last year).
  const availableOptions = useMemo(() => {
    if (!selectedFiliereData || !selectedNiveau) return [];
    return (selectedFiliereData.options || []).filter(
      o => o.isActive && (o.startNiveau ?? selectedFiliereData.nombreAnnees) <= selectedNiveau,
    );
  }, [selectedFiliereData, selectedNiveau]);

  // The filière id sent to the backend: the option when one is picked (narrows
  // to that option), otherwise the parent (which the backend expands to itself
  // + all its options).
  const effectiveFiliereId = selectedOption || selectedFiliere;

  // Filter students by search
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const search = studentSearch.toLowerCase();
    return students.filter(s =>
      s.firstName.toLowerCase().includes(search) ||
      s.lastName.toLowerCase().includes(search) ||
      s.studentCode.toLowerCase().includes(search)
    );
  }, [students, studentSearch]);

  // Load initial options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [years, campusInfo] = await Promise.all([
          getAcademicYears(),
          getCampusInfo(),
        ]);
        setAcademicYears(years);
        setCampuses(campusInfo.campuses.map(c => ({
          value: c.id,
          label: c.name,
        })));

        // Set default academic year to current
        const currentYear = years.find(y => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYear(currentYear.value);
        }

        // Load parent filières with their nested options (parents only in the
        // dropdown; options are surfaced by niveau).
        const fils = await fetchFilieresGroupedForTuition();
        setFilieres(fils);
      } catch (err) {
        console.error('Error loading options:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  // Reset the filière-dependent selections when campus changes (the filière
  // list itself is tenant-wide; students are re-scoped to the campus below).
  useEffect(() => {
    setSelectedFiliere('');
    setSelectedNiveau('');
    setSelectedOption('');
    setSelectedGroup('');
    setSelectedStudents([]);
    setGroups([]);
  }, [selectedCampus]);

  // Reset the chosen option whenever the filière or niveau changes.
  useEffect(() => {
    setSelectedOption('');
  }, [selectedFiliere, selectedNiveau]);

  // Load groups when filiere/niveau/academicYear/campus changes
  useEffect(() => {
    const loadGroups = async () => {
      if (!selectedFiliere || !selectedAcademicYear) {
        setGroups([]);
        setSelectedGroup('');
        return;
      }

      setIsLoadingGroups(true);
      setSelectedGroup('');

      try {
        const merged = await loadFamilyGroups({
          parentFiliereId: selectedFiliere,
          options: (selectedFiliereData?.options || []).map((o) => ({
            id: o.id,
            optionName: o.optionName,
            filiere: o.filiere,
          })),
          selectedOptionId: selectedOption,
          academicYearId: selectedAcademicYear,
          campusId: selectedCampus || undefined,
          niveau: selectedNiveau,
        });
        setGroups(merged);
      } catch (err) {
        console.error('Error loading groups:', err);
        setGroups([]);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    loadGroups();
  }, [selectedFiliere, selectedFiliereData, selectedOption, selectedNiveau, selectedAcademicYear, selectedCampus]);

  // Load students when filters change - load all students by default, filters narrow down results
  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedAcademicYear) return;

      setIsLoadingStudents(true);
      setSelectedStudents([]);

      try {
        const data = await fetchStudentsForFeeAssignment({
          academicYearId: selectedAcademicYear,
          campusId: selectedCampus || undefined,
          filiereId: effectiveFiliereId || undefined,
          niveau: selectedNiveau || undefined,
          groupId: selectedGroup || undefined,
        });
        setStudents(data);
      } catch (err) {
        console.error('[AncillaryFeeAssignModal] Error loading students:', err);
      } finally {
        setIsLoadingStudents(false);
      }
    };

    loadStudents();
  }, [selectedAcademicYear, selectedCampus, selectedFiliere, selectedOption, selectedNiveau, selectedGroup]);

  const handleAssign = async () => {
    if (!selectedAcademicYear) {
      setError('Veuillez sélectionner une année académique');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      let result;

      // Determine assignment scope based on selection
      if (selectedStudents.length > 0) {
        // Assign to specific students
        result = await assignToStudentsMutation.mutateAsync({
          ancillaryFeeId: fee.id,
          studentIds: selectedStudents,
          academicYearId: selectedAcademicYear,
          allowMultiple,
          paymentMode,
        });
        setSuccess(`Frais affecté à ${result.studentsAffected} étudiant(s)`);
      } else if (selectedGroup) {
        // Assign to group (via filiere with group filter). effectiveFiliereId
        // is the option when one is chosen, else the parent (backend expands
        // the parent to itself + all its options).
        result = await assignToFiliereMutation.mutateAsync({
          ancillaryFeeId: fee.id,
          filiereId: effectiveFiliereId,
          niveau: selectedNiveau || undefined,
          academicYearId: selectedAcademicYear,
          groupId: selectedGroup,
          campusId: selectedCampus || undefined,
          allowMultiple,
          paymentMode,
        });
        setSuccess(`Frais affecté à ${result.studentsAffected} étudiant(s) du groupe`);
      } else if (selectedFiliere) {
        // Assign to filiere/option (optionally with niveau)
        result = await assignToFiliereMutation.mutateAsync({
          ancillaryFeeId: fee.id,
          filiereId: effectiveFiliereId,
          niveau: selectedNiveau || undefined,
          academicYearId: selectedAcademicYear,
          campusId: selectedCampus || undefined,
          allowMultiple,
          paymentMode,
        });
        setSuccess(`Frais affecté à ${result.studentsAffected} étudiant(s)`);
      } else if (selectedCampus) {
        // Assign to campus
        result = await assignToCampusMutation.mutateAsync({
          ancillaryFeeId: fee.id,
          campusId: selectedCampus,
          academicYearId: selectedAcademicYear,
          allowMultiple,
          paymentMode,
        });
        setSuccess(`Frais affecté à ${result.studentsAffected} étudiant(s) du campus`);
      } else {
        setError('Veuillez sélectionner au moins un critère (campus, filière, groupe ou étudiants)');
        return;
      }

      refetchAssignments();
      // Clear selections after successful assignment
      setSelectedStudents([]);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  const handleRemoveAssignment = (assignmentId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer l\'affectation',
      message: 'Êtes-vous sûr de vouloir supprimer cette affectation ? Les frais seront retirés des comptes étudiants concernés.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await removeAssignmentMutation.mutateAsync(assignmentId);
          refetchAssignments();
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          setError(err.message || 'Erreur lors de la suppression');
        }
      },
    });
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const getScopeLabel = (assignment: AncillaryFeeAssignment) => {
    switch (assignment.scope) {
      case 'STUDENT':
        return assignment.studentName || 'Étudiant';
      case 'FILIERE':
        return `Filière: ${assignment.filiereName || 'N/A'}`;
      case 'LEVEL':
        return `${assignment.filiereName || 'N/A'} - ${assignment.niveau}ème année`;
      case 'CAMPUS':
        return `Campus: ${assignment.campusName || 'N/A'}`;
      default:
        return '-';
    }
  };

  const getScopeBadgeColor = (scope: string) => {
    switch (scope) {
      case 'STUDENT':
        return 'bg-blue-100 text-blue-700';
      case 'FILIERE':
      case 'LEVEL':
        return 'bg-purple-100 text-purple-700';
      case 'CAMPUS':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const tabs = [
    { id: 'assign' as TabType, label: 'Nouvelle affectation', icon: Users },
    { id: 'history' as TabType, label: 'Historique', icon: History },
  ];

  // Calculate what will be assigned based on current selection
  const getAssignmentPreview = () => {
    if (selectedStudents.length > 0) {
      return `${selectedStudents.length} étudiant(s) sélectionné(s)`;
    }
    if (selectedGroup) {
      const group = groups.find(g => g.value === selectedGroup);
      return `Groupe: ${group?.label || 'Sélectionné'}`;
    }
    if (selectedFiliere) {
      const filiere = filieres.find(f => f.id === selectedFiliere);
      const option = selectedOption
        ? (filiere?.options || []).find(o => o.id === selectedOption)
        : undefined;
      const base = option ? (option.optionName || option.filiere) : (filiere?.filiere || 'Filière');
      if (selectedNiveau) {
        return `${base} - ${selectedNiveau === 1 ? '1ère' : `${selectedNiveau}ème`} année`;
      }
      return `Filière: ${base}`;
    }
    if (selectedCampus) {
      const campus = campuses.find(c => c.value === selectedCampus);
      return `Campus: ${campus?.label || 'Sélectionné'}`;
    }
    return 'Sélectionnez des critères';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Gérer les affectations
                </h2>
                <p className="text-sm text-gray-500">
                  {fee.name} - {formatCurrency(fee.amount)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setError(null);
                  setSuccess(null);
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Messages */}
            {error && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle size={18} />
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <Check size={18} />
                {success}
              </div>
            )}

            {isLoadingOptions ? (
              <div className="text-center py-8">
                <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                <p className="mt-2 text-gray-500">Chargement...</p>
              </div>
            ) : (
              <>
                {/* Assign Tab */}
                {activeTab === 'assign' && (
                  <div className="space-y-6">
                    {/* Filters Section */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center justify-between w-full text-left"
                      >
                        <div className="flex items-center gap-2">
                          <Filter size={18} className="text-gray-500" />
                          <span className="font-medium text-gray-700">Filtres de sélection</span>
                        </div>
                        {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>

                      {showFilters && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Academic Year */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Année académique <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={selectedAcademicYear}
                              onChange={(e) => setSelectedAcademicYear(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">Sélectionner</option>
                              {academicYears.map((year) => (
                                <option key={year.value} value={year.value}>
                                  {year.label} {year.isCurrent ? '(En cours)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Campus */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Building2 size={14} className="inline mr-1" />
                              Campus (optionnel)
                            </label>
                            <select
                              value={selectedCampus}
                              onChange={(e) => setSelectedCampus(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">Tous les campus</option>
                              {campuses.map((campus) => (
                                <option key={campus.value} value={campus.value}>
                                  {campus.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Filiere */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <GraduationCap size={14} className="inline mr-1" />
                              Filière (optionnel)
                            </label>
                            <select
                              value={selectedFiliere}
                              onChange={(e) => setSelectedFiliere(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                            >
                              <option value="">Toutes les filières</option>
                              {filieres.map((fil) => (
                                <option key={fil.id} value={fil.id}>
                                  {fil.filiere}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Niveau */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Layers size={14} className="inline mr-1" />
                              Niveau (optionnel)
                            </label>
                            <select
                              value={selectedNiveau}
                              onChange={(e) => setSelectedNiveau(e.target.value ? parseInt(e.target.value) : '')}
                              disabled={!selectedFiliere || niveauOptions.length === 0}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                            >
                              <option value="">Tous les niveaux</option>
                              {niveauOptions.map((n) => (
                                <option key={n} value={n}>
                                  {n === 1 ? '1ère année' : `${n}ème année`}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Option — only shown once a niveau with options is selected.
                              Leaving it on "Toutes les options" affects every option
                              student of the parent at this niveau. */}
                          {availableOptions.length > 0 && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                <GraduationCap size={14} className="inline mr-1" />
                                Option (optionnel)
                              </label>
                              <select
                                value={selectedOption}
                                onChange={(e) => setSelectedOption(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="">Toutes les options</option>
                                {availableOptions.map((opt) => (
                                  <option key={opt.id} value={opt.id}>
                                    {opt.optionName || opt.filiere}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Group */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <UsersRound size={14} className="inline mr-1" />
                              Groupe (optionnel)
                            </label>
                            <select
                              value={selectedGroup}
                              onChange={(e) => setSelectedGroup(e.target.value)}
                              disabled={!selectedFiliere || isLoadingGroups}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                            >
                              <option value="">Tous les groupes</option>
                              {groups.map((grp) => (
                                <option key={grp.value} value={grp.value}>
                                  {grp.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Students Selection */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <User size={18} className="text-gray-500" />
                            <span className="font-medium text-gray-700">
                              Sélection individuelle (optionnel)
                            </span>
                            {students.length > 0 && (
                              <span className="text-sm text-gray-500">
                                ({students.length} étudiant{students.length > 1 ? 's' : ''} trouvé{students.length > 1 ? 's' : ''})
                              </span>
                            )}
                          </div>
                          {students.length > 0 && (
                            <button
                              onClick={selectAllStudents}
                              className="text-sm text-primary-600 hover:text-primary-700"
                            >
                              {selectedStudents.length === filteredStudents.length ? 'Désélectionner tout' : 'Tout sélectionner'}
                            </button>
                          )}
                        </div>
                        {students.length > 0 && (
                          <div className="mt-2 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="text"
                              placeholder="Rechercher un étudiant..."
                              value={studentSearch}
                              onChange={(e) => setStudentSearch(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        )}
                      </div>

                      <div className="max-h-64 overflow-y-auto">
                        {isLoadingStudents ? (
                          <div className="p-8 text-center">
                            <RefreshCw className="mx-auto h-6 w-6 text-gray-400 animate-spin" />
                            <p className="mt-2 text-sm text-gray-500">Chargement des étudiants...</p>
                          </div>
                        ) : filteredStudents.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                            {filteredStudents.map((student) => (
                              <label
                                key={student.id}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedStudents.includes(student.id)}
                                  onChange={() => toggleStudentSelection(student.id)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                  {student.photoUrl ? (
                                    <img src={student.photoUrl} alt="" className="h-full w-full object-cover" />
                                  ) : (
                                    <User size={16} className="text-gray-500" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">{student.studentCode}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="p-8 text-center text-gray-500">
                            <User className="mx-auto h-8 w-8 text-gray-300" />
                            <p className="mt-2 text-sm">
                              {isLoadingStudents
                                ? 'Chargement des étudiants...'
                                : 'Aucun étudiant trouvé avec ces critères'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment mode: how the fee lands in the schedule */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-800 mb-3">
                        Comment ce frais doit-il être payé&nbsp;?
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMode('DISTRIBUTE')}
                          className={`text-left p-3 rounded-lg border transition-colors ${
                            paymentMode === 'DISTRIBUTE'
                              ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="block text-sm font-medium text-gray-900">
                            Répartir sur les mensualités
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Le montant est réparti sur les échéances non encore payées de l'année.
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMode('IMMEDIATE')}
                          className={`text-left p-3 rounded-lg border transition-colors ${
                            paymentMode === 'IMMEDIATE'
                              ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="block text-sm font-medium text-gray-900">
                            À payer immédiatement
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Le montant est ajouté à la prochaine échéance à payer.
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Allow Multiple Option */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allowMultiple}
                          onChange={(e) => setAllowMultiple(e.target.checked)}
                          className="mt-0.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Copy size={16} className="text-amber-600" />
                            <span className="font-medium text-amber-900">
                              Autoriser les affectations multiples
                            </span>
                          </div>
                          <p className="text-sm text-amber-700 mt-1">
                            Si activé, ce frais sera ajouté même s'il a déjà été affecté aux étudiants sélectionnés.
                            Le montant s'ajoutera au solde existant.
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Assignment Preview (the Affecter action lives in the
                        sticky footer so it's always reachable without scrolling) */}
                    <div className="bg-primary-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-primary-900">Affectation prévue</p>
                      <p className="text-sm text-primary-700">{getAssignmentPreview()}</p>
                    </div>
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div>
                    {isLoadingAssignments ? (
                      <div className="text-center py-8">
                        <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
                        <p className="mt-2 text-gray-500">Chargement de l'historique...</p>
                      </div>
                    ) : assignmentsData?.data && assignmentsData.data.length > 0 ? (
                      <div className="space-y-3">
                        {assignmentsData.data.map((assignment) => (
                          <div
                            key={assignment.id}
                            className={`p-4 rounded-lg border ${
                              assignment.isActive
                                ? 'bg-white border-gray-200'
                                : 'bg-gray-50 border-gray-200 opacity-60'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${getScopeBadgeColor(assignment.scope)}`}>
                                    {assignment.scope === 'STUDENT' ? 'Étudiant' :
                                     assignment.scope === 'FILIERE' ? 'Filière' :
                                     assignment.scope === 'LEVEL' ? 'Niveau' : 'Campus'}
                                  </span>
                                  {!assignment.isActive && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                                      Supprimé
                                    </span>
                                  )}
                                </div>
                                <p className="font-medium text-gray-900 mt-1">
                                  {getScopeLabel(assignment)}
                                </p>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {assignment.academicYearName} • Affecté par {assignment.assignedByName || 'N/A'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {new Date(assignment.assignedAt).toLocaleDateString('fr-FR', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </div>
                              {assignment.isActive && (
                                <button
                                  onClick={() => handleRemoveAssignment(assignment.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Supprimer cette affectation"
                                  disabled={removeAssignmentMutation.isPending}
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <History className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune affectation</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Ce frais n'a pas encore été affecté à des étudiants.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
              {activeTab === 'assign' && (
                <button
                  onClick={handleAssign}
                  disabled={isSubmitting || !selectedAcademicYear || (!selectedCampus && !selectedFiliere && selectedStudents.length === 0)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Affectation...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Affecter
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </>
  );
};

export default AncillaryFeeAssignModal;
